import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { APP_SHORT_LABELS, isAppKey, type AppKey } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { ConfirmButton } from '../../components/ConfirmButton.js';
import { ApiError } from '../../lib/api.js';
import { createDelegation, fetchMyDelegations, revokeDelegation } from '../../lib/rbac-admin-api.js';
import { t, formatDate } from '../../lib/i18n.js';

/**
 * My Delegations (NEW-10). Delegating is its own permission: someone going on
 * leave hands their application roles to a colleague for a set time. It used to
 * be possible only from the access-administration page, which a QA/QC
 * Representative cannot open. Here a person sees what they have given and been
 * given, and — if they may delegate — gives or ends one.
 */
export function MyDelegations() {
  const { app: rawApp } = useParams();
  const app: AppKey = isAppKey(rawApp) ? rawApp : 'qaqc';
  const queryClient = useQueryClient();
  const mine = useQuery({ queryKey: ['my-delegations'], queryFn: fetchMyDelegations });
  const [toUserId, setToUserId] = useState('');
  const [forApp, setForApp] = useState<string>(app);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['my-delegations'] });
  const fail = (fallback: string) => (caught: unknown) => setError(caught instanceof ApiError ? caught.message : fallback);

  const create = useMutation({
    mutationFn: () =>
      createDelegation({
        toUserId,
        app: forApp,
        businessUnitIds: [],
        projectIds: [],
        startsAt,
        endsAt,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setError(null);
      setToUserId('');
      setReason('');
      refresh();
    },
    onError: fail(t("The delegation could not be made.")),
  });

  const end = useMutation({
    mutationFn: (id: string) => revokeDelegation(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("The delegation could not be ended.")),
  });

  const data = mine.data;
  const now = Date.now();

  return (
    <div className="min-h-full">
      <AppTopBar app={app} />
      <Breadcrumbs app={app} trail={['My delegations']} />
      <main className="mx-auto max-w-4xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("My delegations")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("A delegation hands your application roles to a colleague between two dates — for leave, say. Everything they do with it is recorded as done on your behalf.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm text-tone-critical">
            {error}
          </p>
        )}
        {mine.error && (
          <p role="alert" className="mt-4 text-sm text-tone-critical">
            {t("Your delegations could not be loaded.")}
          </p>
        )}

        {data && data.delegateApps.length > 0 && (
          <form
            aria-label={t("New delegation")}
            className="mt-6 grid gap-3 rounded-md border border-border bg-surface p-5 text-sm sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <h2 className="m-0 text-base font-semibold sm:col-span-2">{t("Delegate my roles")}</h2>
            <label className="flex flex-col">
              <span className="mb-1 text-content-secondary">{t("To")}</span>
              <select
                className="rounded-sm border border-border-strong px-2 py-1.5"
                value={toUserId}
                onChange={(event) => setToUserId(event.target.value)}
              >
                <option value="">{t("Choose a colleague")}</option>
                {data.people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="mb-1 text-content-secondary">{t("In")}</span>
              <select
                className="rounded-sm border border-border-strong px-2 py-1.5"
                value={forApp}
                onChange={(event) => setForApp(event.target.value)}
              >
                {data.delegateApps.map((key) => (
                  <option key={key} value={key}>
                    {APP_SHORT_LABELS[key as AppKey] ?? key}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="mb-1 text-content-secondary">{t("From")}</span>
              <input
                type="date"
                className="rounded-sm border border-border-strong px-2 py-1.5"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
              />
            </label>
            <label className="flex flex-col">
              <span className="mb-1 text-content-secondary">{t("Until (the end of that day)")}</span>
              <input
                type="date"
                className="rounded-sm border border-border-strong px-2 py-1.5"
                value={endsAt}
                onChange={(event) => setEndsAt(event.target.value)}
              />
            </label>
            <label className="flex flex-col sm:col-span-2">
              <span className="mb-1 text-content-secondary">{t("Why")}</span>
              <input
                className="rounded-sm border border-border-strong px-2 py-1.5"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <button
              type="submit"
              className="w-fit rounded-sm bg-ag-purple px-4 py-2 font-medium text-content-inverse disabled:opacity-60"
              disabled={create.isPending || !toUserId || !startsAt || !endsAt || !reason.trim()}
            >
              {t("Delegate")}
            </button>
          </form>
        )}

        <h2 className="mt-8 text-base font-semibold">{t("Given and received")}</h2>
        {data && data.delegations.length === 0 && (
          <p className="text-sm text-content-secondary">{t("You have not given or received a delegation.")}</p>
        )}
        <ul className="mt-2 list-none space-y-2 p-0">
          {(data?.delegations ?? []).map((delegation) => {
            const live = !delegation.revokedAt && new Date(delegation.endsAt).getTime() > now;
            return (
              <li key={delegation.id} className="rounded-md border border-border bg-surface p-4 text-sm">
                <p className="m-0 font-medium">
                  {delegation.direction === 'given' ? t("To") : t("From")} {delegation.otherParty ?? t("a former colleague")}
                  {delegation.app ? ` · ${APP_SHORT_LABELS[delegation.app as AppKey] ?? delegation.app}` : ''}
                </p>
                <p className="m-0 mt-1 text-content-secondary">
                  {formatDate(delegation.startsAt)} – {formatDate(delegation.endsAt)}
                  {delegation.revokedAt ? t(" · ended early") : live ? '' : t(" · finished")}
                </p>
                <p className="m-0 mt-1">{delegation.reason}</p>
                {live && (
                  <ConfirmButton
                    label={t("End it now")}
                    question={t("End this delegation now?")}
                    confirmLabel={t("Yes, end it")}
                    className="mt-2 rounded-sm border border-border-strong px-2 py-1 text-xs"
                    disabled={end.isPending}
                    onConfirm={() => end.mutate(delegation.id)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}
